// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { miniMyAccountStyles } from "@rpsc/App/Components/MiniMyAccount/MiniMyAccount";
import IconLogout from "@rpsc/XDL/Icons/IconLogout";
import IconPencilEdit from "@rpsc/XDL/Icons/IconPencilEdit";
import { fontConfig } from "@rpsc/XDL/PreStyle/typography";
import { color, createLinkIconStyles, styledScrollbar } from "@rpsc/XDL/Utilities";
import { css } from "styled-components";

const sharedSectionTitleStyle: TypographyPresentationProps = {
    variant: "h6",
    forwardAs: "span",
    size: 14,
    lineHeight: "19px",
    css: css`
        width: 100%;
    `,
};

miniMyAccountStyles.wrapper = {
    css: css`
        display: flex;
        flex-direction: column;
        width: 432px;
    `,
};

miniMyAccountStyles.container = {
    css: css`
        padding: 12px 12px 0;
        overflow: auto;
        max-height: 60vh;
        ${styledScrollbar()}
    `,
};

miniMyAccountStyles.titleWrapper = {
    css: css`
        margin-bottom: 12px;
        padding-bottom: 8px;
        border-bottom: 1px solid ${color("common.border")};
    `,
};

miniMyAccountStyles.title = {
    variant: "h5",
    css: css`
        margin-bottom: 0;
    `,
};

miniMyAccountStyles.personalInfo = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 24px;
        `,
    },
    infoWrapper: {
        gap: 16,
    },
    infoColumnContainer: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    title: {
        ...sharedSectionTitleStyle,
        css: css`
            margin-bottom: 0;
        `,
    },
    subTitle: {
        size: 12,
        lineHeight: "14px",
        weight: fontConfig.weight.bold,
        css: css`
            margin-bottom: 8px;
            ${wrapInContainerStyles}
        `,
    },
    name: {
        size: 12,
        lineHeight: "13px",
        css: css`
            margin-bottom: 4px;
            ${wrapInContainerStyles}
        `,
    },
    label: {
        lineHeight: "13px",
        css: css`
            margin-bottom: 4px;
        `,
    },
    accountLink: {
        css: css`
            margin-bottom: 4px;
            &&&:hover ${TypographyStyle}, &:hover svg {
                color: ${color("common.ui8")};
            }
        `,
    },
};

miniMyAccountStyles.linkItems = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            gap: 12px;
            margin-bottom: 24px;
        `,
    },
    title: {
        ...sharedSectionTitleStyle,
        css: css`
            margin-bottom: 0;
        `,
    },
    linksContainer: {
        gap: 12,
    },
    linkWrapper: {
        width: 6,
    },
    link: {
        typographyProps: {
            size: 12,
            weight: fontConfig.weight.bold,
            css: css`
                line-height: 13px;
            `,
        },
    },
};

miniMyAccountStyles.actionSection = {
    wrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            padding: 12px;
            border-top: 1px solid ${color("common.border")};
        `,
    },
    signOutButton: {
        ...createLinkIconStyles(IconLogout),
    },
    changeShipTo: {
        ...createLinkIconStyles(IconPencilEdit),
    },
};

const overriddenTextStyle = {
    size: 12,
    lineHeight: "17px",
};

miniMyAccountStyles.addressStyles = {
    address1Text: overriddenTextStyle,
    address2Text: overriddenTextStyle,
    attentionText: overriddenTextStyle,
    cityText: overriddenTextStyle,
    postalCodeText: overriddenTextStyle,
    stateText: overriddenTextStyle,
    firstNameText: overriddenTextStyle,
    lastNameText: overriddenTextStyle,
    phoneText: overriddenTextStyle,
};
